import i18n from 'i18n-js';
import { Animated } from 'react-native';
import { customColors } from '../../../commons/tests/mocks/mockdata/colors';
import { ITEMS_PER_MINUTE_WINDOW_DAYS } from '../../../constants';

const ALL_PACKERS_COLOR = customColors.allPackersColor;

const formatDateLabel = dateString => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  const monthIndex = date.getMonth();
  const month = i18n.t(`months.short.${monthIndex}`);
  const day = date.getDate();
  return `${month} ${day}`;
};

const formatHourTo12Hour = hourStr => {
  if (!hourStr || typeof hourStr !== 'string') {
    return hourStr || '';
  }
  try {
    const normalizedHour = hourStr.trim();

    const hourWithPeriod = normalizedHour.match(
      /^(\d{1,2})(?::\d{2})?\s*([AaPp][Mm])$/
    );
    if (hourWithPeriod) {
      const hours = Number(hourWithPeriod[1]);
      const period = hourWithPeriod[2].toLowerCase();
      if (Number.isNaN(hours) || hours < 1 || hours > 12) {
        return hourStr;
      }
      return `${hours}${period}`;
    }

    const [hours] = normalizedHour.split(':').map(Number);
    if (Number.isNaN(hours) || hours < 0 || hours > 23) {
      return hourStr;
    }
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours >= 12 ? 'pm' : 'am';
    return `${hour12}${period}`;
  } catch (e) {
    return hourStr;
  }
};

const parseTimeToMinutes = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') {
    return null;
  }

  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
};

const getSlotDurationMinutes = (start, end) => {
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);

  if (startMinutes === null || endMinutes === null) {
    return null;
  }

  let diff = endMinutes - startMinutes;
  if (diff <= 0) {
    diff += 24 * 60;
  }

  return diff || 60;
};

const normalizeBucketMinutes = bucket => {
  if (!bucket) {
    return 0;
  }

  const slotMinutes = getSlotDurationMinutes(
    bucket.bucket_start,
    bucket.bucket_end
  );
  const rawMinutes = Number(bucket.minutes);

  if (Number.isFinite(rawMinutes) && rawMinutes > 0) {
    if (slotMinutes && rawMinutes > slotMinutes * 1.25) {
      return slotMinutes;
    }
    return rawMinutes;
  }

  return slotMinutes || 0;
};

const normalizeTimeSeriesMinutes = series => {
  if (!Array.isArray(series) || !series.length) {
    return 0;
  }

  return series.reduce((total, bucket) => total + normalizeBucketMinutes(bucket), 0);
};

export function getUserInfo(userId, userDetails) {
  const user = userDetails?.find(user => user.id == userId);
  if (user) {
    return { label: user.name, color: user.color };
  } else {
    return null;
  }
}

export function isBarTallEnough(barHeight, threshold = 25) {
  return barHeight > threshold;
}

export function calculateBarWidth(
  availableWidth,
  dataCount,
  defaultWidth,
  minWidth,
  userThreshold,
  spacing
) {
  const totalBarsWidth = dataCount * (defaultWidth + spacing);
  const useMinWidth = totalBarsWidth > availableWidth && dataCount > userThreshold;
  return useMinWidth ? minWidth : defaultWidth;
}

export function calculateMaxValue(values, minimumMaxValue = 10) {
  return Math.max(...values, minimumMaxValue);
}

export function generateYAxisValues(maxValue, segments = 6) {
  const values = [];
  for (let i = 0; i <= segments; i++) {
    values.push(Math.round((maxValue * (segments - i)) / segments));
  }
  return values;
}

export function calculateChartDimensions(
  containerDimensions,
  dataList,
  barWidth,
  spacing,
  yAxisWidth,
  legendWidth
) {
  const availableWidth = containerDimensions.width - yAxisWidth - legendWidth - 20;
  const calculatedChartWidth = Math.max(
    dataList.length * (barWidth + spacing),
    availableWidth
  );
  
  return {
    chartWidth: calculatedChartWidth,
    shouldScroll: calculatedChartWidth > availableWidth
  };
}

export function calculateSum(dataList, defaultValue = 15) {
  return dataList?.reduce((sum, item) => sum + (item.scanned_item_count || item.item_count), 0) || defaultValue;
}

export function getOptimalFontSize(height) {
  if (height < 20) return 0;
  if (height < 30) return 12;
  if (height < 45) return 14;
  return 26;
}

export function generateGridLines(chartHeight, chartWidth, segments = 6, color = "rgba(255,255,255,0.1)") {
  const lines = [];
  
  for (let i = 0; i <= segments; i++) {
    const y = (i * chartHeight) / segments;
    lines.push({
      key: `line-${i}`,
      x1: 0,
      y1: y,
      x2: chartWidth,
      y2: y,
      stroke: color,
      strokeWidth: 1,
      strokeDasharray: color === customColors.gridLineColor ? "5,5" : undefined
    });
  }

  return lines;
}

export function getColorIntensity(value, max = 100) {
  const intensity = Math.floor((value / max) * 255);
  return `rgb(${255 - intensity}, ${255 - intensity}, 255)`;
}

export function processBarChartData(statistics, userDetails, calculateFn) {
  if (!statistics?.length) return { statistics: [], labels: [] };

  const processedData = statistics.reduce(
    (acc, stat) => {
      const userId = parseInt(stat.user_id);
      const value = calculateFn(stat.data);
      const user = getUserInfo(userId, userDetails);
      
      if (user) {
        acc.statistics.push({
          ...user,
          value
        });
      }
      return acc;
    },
    { statistics: [] }
  );

  const labels = processedData.statistics.map(item => ({
    label: item.label,
    color: item.color
  }));

  return { 
    statistics: processedData.statistics,
    labels
  };
}

export const shadeColor = (color, percent) => {
  try {
    if (!color || typeof color !== 'string' || !color.startsWith('#') || color.length !== 7) {
      return '#cccccc';
    }
    
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    R = Math.floor(R * (100 + percent) / 100);
    G = Math.floor(G * (100 + percent) / 100);
    B = Math.floor(B * (100 + percent) / 100);

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    R = R > 0 ? R : 0;
    G = G > 0 ? G : 0;
    B = B > 0 ? B : 0;

    const RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16);
    const GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16);
    const BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16);

    return `#${RR}${GG}${BB}`;
  } catch (error) {
    console.error('Error in shadeColor:', error);
    return '#cccccc';
  }
};

export const lightenColor = (color, percent = 30) => {
  return shadeColor(color, percent);
};

export const calculateBarTodayVsYesDimensions = (
  groupIndex, 
  group, 
  maxValue, 
  chartHeight, 
  groupWidth, 
  groupSpacing, 
  todayBarWidth, 
  yesterdayBarWidth, 
  barSpacing
) => {
  const groupX = groupIndex * (groupWidth + groupSpacing);
  
  const yesterdayBarHeight = (group.yesterday.value / maxValue) * chartHeight ||
    0;
  const yesterdayBarX = groupX;
  const yesterdayBarY = chartHeight - yesterdayBarHeight;
  const yesterdayBarCenter = yesterdayBarX + yesterdayBarWidth / 2;
  
  const todayBarHeight = (group.today.value / maxValue) * chartHeight || 0;
  const todayBarX = groupX + yesterdayBarWidth + barSpacing;
  const todayBarY = chartHeight - todayBarHeight;
  const todayBarCenter = todayBarX + todayBarWidth / 2;
  
  const veryTallBar = todayBarHeight > 200;
  const tallBar = todayBarHeight > 80 && todayBarHeight <= 200;
  const shortBar = todayBarHeight <= 40;
  
  return {
    groupX,
    yesterday: {
      height: yesterdayBarHeight,
      x: yesterdayBarX,
      y: yesterdayBarY,
      center: yesterdayBarCenter
    },
    today: {
      height: todayBarHeight,
      x: todayBarX,
      y: todayBarY,
      center: todayBarCenter
    },
    barType: {
      veryTall: veryTallBar,
      tall: tallBar,
      short: shortBar
    }
  };
};

export const processHorizontalChartStatistics = (
  response,
  dataProcessedRef,
  setMaxValue,
  setDataList,
  sortOrder = 'desc',
  metric = 'items'
) => {
  if (
    dataProcessedRef.current ||
    !response?.data?.statistics ||
    !response?.data?.user_details
  )
    return false;

  try {
    dataProcessedRef.current = true;

    const {statistics: statisticsList, user_details: userDetails} = response.data;

    if (!statisticsList || !userDetails) return false;

    const userMap = {};
    userDetails.forEach(user => {
      userMap[user.id] = user;
    });

    const processedData = statisticsList.map(userStat => {
      const userId = parseInt(userStat.user_id, 10);
      const user = userMap[userId];
      
      if (!user) return null;

      let value = 0;
      if (metric === 'orders') {
        value = userStat.data?.today_details?.today_data?.length || 0;
      } else {
        value = userStat.items_per_minute || 0;
      }
      const mainColor = user.color || '#cccccc';
      const lighterColor = lightenColor(mainColor, 30);
      const darkerColor = shadeColor(mainColor, -30);

      return {
        userId,
        label: user.name || `User ${userId}`,
        value: value,
        color: mainColor,
        gradientStart: lighterColor,
        gradientEnd: darkerColor
      };
    }).filter(Boolean);

    if (!processedData || processedData.length === 0) {
      dataProcessedRef.current = false;
      return false;
    }

    const maxVal = calculateMaxValue(
      processedData.map(item => item.value),
      0
    );
    setMaxValue(Math.ceil(maxVal * 1.05));

    const sortedData = [...processedData];
    if (sortOrder === 'asc') {
      sortedData.sort((a, b) => a.value - b.value);
    } else {
      sortedData.sort((a, b) => b.value - a.value);
    }
    
    setDataList(sortedData);
    
    return true;
  } catch (error) {
    console.error('Error processing statistics:', error);
    dataProcessedRef.current = false;
    return false;
  }
};

export const calculateRemainingTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();

  const currentQuarter = Math.floor(minutes / 15);
  const nextQuarterMinute = (currentQuarter + 1) * 15;

  let remainingMinutes = nextQuarterMinute - minutes - 1;
  let remainingSeconds = 60 - seconds;
  
  if (remainingSeconds === 60) {
    remainingSeconds = 0;
    remainingMinutes += 1;
  }

  const formattedText = `${String(remainingMinutes).padStart(2, '0')} min ${String(remainingSeconds).padStart(2, '0')} sec Remaining`;
  
  return {
    text: formattedText,
    minutes: remainingMinutes,
    seconds: remainingSeconds
  };
};

export const getCurrentQuarter = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  return Math.floor(minutes / 15);
};

export const processDailyChartStatistics = (response, metric = 'items') => {
  if (!response?.data?.statistics) {
    return { 
      dataList: [], 
      maxValue: 0, 
      success: false 
    };
  }

  try {
    const { statistics: statisticsList, user_details: userDetails } = response.data;

    if (!statisticsList || !statisticsList.length) {
      return { 
        dataList: [], 
        maxValue: 0, 
        success: true 
      };
    }

    const { data } = statisticsList.reduce(
      (acc, { data, user_id, orders_scanned, items_scanned }) => {
        try {
          let calculateSum = 0;
          if (metric === 'orders') {
            calculateSum = orders_scanned !== undefined ? orders_scanned : (data?.length || 0);
          } else if (metric === 'items') {
            calculateSum = items_scanned !== undefined ? items_scanned : data.reduce(
              (total, item) => total + (item?.scan_order_item_count || item?.item_count || 0),
              0
            );
          } else {
            calculateSum = items_scanned !== undefined ? items_scanned : data.reduce(
              (total, item) => total + (item?.scanned_item_count || item?.item_count || 0),
              0
            );
          }
          const userInfo = getUserInfo(user_id, userDetails) || 
                          { label: `User ${user_id}`, color: '#cccccc' };
          
          acc.data.push({ value: calculateSum || 0, ...userInfo });
        } catch (e) {
          console.error('Error processing statistic item:', e);
        }
        return acc;
      },
      { data: [] }
    );

    const sortedData = [...data].sort((a, b) => a.value - b.value);

    const enhancedData = sortedData.map(item => {
      const mainColor = item.color || '#cccccc';
      const lighterColor = lightenColor(mainColor, 30);
      const darkerColor = shadeColor(mainColor, -30);

      let limitedLabel = item.label || '';
      if (limitedLabel.length > 10) {
        limitedLabel = limitedLabel.substring(0, 10) + '...';
      }

      return {
        ...item,
        label: limitedLabel,
        gradientStart: lighterColor,
        gradientEnd: darkerColor
      };
    });

    const maxVal = Math.max(...enhancedData.map(item => item.value), 1);
    const maxValue = Math.ceil(maxVal * 1.05);

    return {
      dataList: enhancedData,
      maxValue,
      success: true
    };
  } catch (e) {
    console.error('Error processing statistics:', e);
    return { 
      dataList: [], 
      maxValue: 0, 
      success: false,
      error: e 
    };
  }
};

const getScanScoreUserKey = score => {
  return score.user_id || score.packing_user_id || score.userId;
};

export const processScanScoreStatistics = (response, liveScanScoreList = {}) => {
  try {
    const apiScores = response?.data?.statistics || [];
    const userDetails = response?.data?.user_details || [];
    const scoreMap = {};

    apiScores.forEach(score => {
      const userKey = getScanScoreUserKey(score);
      if (!userKey) {
        return;
      }

      const userInfo = getUserInfo(userKey, userDetails);
      scoreMap[userKey] = {
        id: userKey,
        name: userInfo?.label || score.label || score.user_name || `User ${userKey}`,
        score: Number(score.score || 0),
        userInfo
      };
    });

    Object.values(liveScanScoreList || {}).forEach(score => {
      const userKey = getScanScoreUserKey(score);
      if (!userKey) {
        return;
      }

      const liveScore = Number(score.score || 0);
      const existingScore = Number(scoreMap[userKey]?.score || 0);
      const userInfo = scoreMap[userKey]?.userInfo || getUserInfo(userKey, userDetails);
      scoreMap[userKey] = {
        id: userKey,
        name: userInfo?.label || score.name || scoreMap[userKey]?.name || `User ${userKey}`,
        score: Math.max(existingScore, liveScore),
        userInfo
      };
    });

    const sortedScores = Object.values(scoreMap).sort((a, b) => b.score - a.score);

    const dataList = sortedScores.map(user => {
      const color = user.userInfo?.color || '#cccccc';
      return {
        name: user.name,
        label: user.userInfo?.label || user.name,
        value: user.score,
        color,
        gradientStart: lightenColor(color, 30),
        gradientEnd: shadeColor(color, -30)
      };
    });

    const maxScore = Math.max(...dataList.map(item => item.value), 1);

    return {
      dataList,
      maxValue: Math.ceil(maxScore * 1.1),
      success: true
    };
  } catch (error) {
    console.error('Error processing scan score statistics:', error);
    return {
      dataList: [],
      maxValue: 0,
      success: false
    };
  }
};

export const calculateDailyChartDimensions = (
  data, 
  containerDimensions, 
  maxBarsToShow = 6, 
  barGapRatio = 0.2, 
  yAxisWidth = 50
) => {
  const availableWidth = containerDimensions.width - yAxisWidth - 40;
  const totalWidthPerBar = availableWidth / maxBarsToShow;
  const calculatedBarWidth = totalWidthPerBar * (1 - barGapRatio);
  const calculatedSpacing = totalWidthPerBar * barGapRatio;

  const calculatedChartWidth = Math.max(
    data.length * totalWidthPerBar,
    containerDimensions.width - 70
  );

  return {
    barWidth: calculatedBarWidth,
    spacing: calculatedSpacing,
    chartWidth: calculatedChartWidth,
    shouldScroll: data.length > maxBarsToShow
  };
};

export const processWithin15MinChartStatistics = (response, metric = 'items') => {
  if (!response?.data?.statistics) {
    return { 
      dataList: [], 
      maxValue: 0, 
      success: false 
    };
  }

  try {
    const { statistics: statisticsList, user_details: userDetails } = response.data;

    if (!statisticsList || !statisticsList.length) {
      return { 
        dataList: [], 
        maxValue: 0, 
        success: true 
      };
    }

    let processedData;
    try {
      processedData = processBarChartData(statisticsList, userDetails, data => {
        if (metric === 'orders') {
          return data?.filter(item => item.status === 'scanned').length || 0;
        } else if (metric === 'items') {
          return data.reduce((total, item) => total + (item?.scan_order_item_count || item?.item_count || 0), 0);
        }
        return data.reduce((total, item) => total + (item?.scanned_item_count || item?.item_count || 0), 0);
      });
    } catch (e) {
      console.error('Error in processBarChartData:', e);
      processedData = { statistics: [] };
    }

    const data = processedData.statistics || [];

    if (data.length === 0) {
      return { 
        dataList: [], 
        maxValue: 0, 
        success: true 
      };
    }

    const sortedData = [...data].sort((a, b) => a.value - b.value);

    const enhancedData = sortedData.map(item => {
      const mainColor = item.color || '#cccccc';
      const lighterColor = lightenColor(mainColor, 30);
      const darkerColor = shadeColor(mainColor, -30);
      
      let limitedLabel = item.label || '';
      if (limitedLabel.length > 10) {
        limitedLabel = limitedLabel.substring(0, 10) + '...';
      }

      return {
        ...item,
        label: limitedLabel,
        gradientStart: lighterColor,
        gradientEnd: darkerColor
      };
    });

    let maxVal;
    try {
      maxVal = calculateMaxValue(
        enhancedData.map(item => item.value),
        10
      ) * 1.1;
    } catch (e) {
      console.error('Error calculating max value:', e);
      maxVal = Math.max(...enhancedData.map(item => item.value || 0), 1) * 1.1;
    }

    return {
      dataList: enhancedData,
      maxValue: maxVal,
      success: true
    };
  } catch (e) {
    console.error('Error processing statistics:', e);
    return { 
      dataList: [], 
      maxValue: 0, 
      success: false,
      error: e 
    };
  }
};

export const processItemsPerMinuteStatistics = (response, metric = 'items') => {
  if (!response?.data?.statistics) {
    return {
      seriesByPacker: {all: []},
      packers: [],
      maxValue: 0,
      success: false
    };
  }

  try {
    const {statistics: statisticsList = [], user_details: userDetails = []} =
      response.data;

    if (!statisticsList.length) {
      return {
        seriesByPacker: {all: []},
        packers: [],
        maxValue: 0,
        success: true
      };
    }

    const sortedStats = statisticsList
      .filter(entry => entry?.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const limitedStats = sortedStats.slice(-ITEMS_PER_MINUTE_WINDOW_DAYS);
    const orderedDates = limitedStats.map(day => ({
      date: day.date,
      label: formatDateLabel(day.date)
    }));

    if (!limitedStats.length) {
      return {
        seriesByPacker: {all: []},
        packers: [],
        maxValue: 0,
        success: true
      };
    }

    const userSeries = {};
    const aggregateSeries = [];
    let globalMaxValue = 0;

    limitedStats.forEach((dayEntry, index) => {
      const dateLabel = orderedDates[index]?.label || formatDateLabel(dayEntry.date);
      let dailyItems = 0;
      let dailyMinutes = 0;
      let dailyOrders = 0;

      (dayEntry?.data || []).forEach(userEntry => {
        const userId = parseInt(
          userEntry.packing_user_id ?? userEntry.user_id,
          10
        );

        if (!userId) {
          return;
        }

        let computedValue = 0;
        if (metric === 'orders') {
          const ordersScanned = Number(userEntry.orders_scanned || 0);
          const minutesWorked = Number(userEntry.scanned_minutes || 0);
          if (minutesWorked > 0) {
            computedValue = ordersScanned / minutesWorked;
          }
          dailyOrders += ordersScanned;
          dailyMinutes += minutesWorked;
        } else if (metric === 'items') {
          computedValue = Number(userEntry.items_per_minute || 0);
          const itemsScanned = Number(userEntry.scanned_item_count || userEntry.scan_order_item_count || userEntry.items_scanned || 0);
          dailyItems += itemsScanned;
          dailyMinutes += Number(userEntry.scanned_minutes || 0);
        } else {
          computedValue = Number(userEntry.items_per_minute || 0);
          dailyItems += Number(userEntry.items_scanned || 0);
          dailyMinutes += Number(userEntry.scanned_minutes || 0);
        }

        if (!userSeries[userId]) {
          const userInfo = getUserInfo(userId, userDetails) || {
            label: `User ${userId}`,
            color: '#cccccc'
          };
          const mainColor = userInfo.color || '#cccccc';

          userSeries[userId] = {
            meta: {
              label: userInfo.label,
              color: mainColor,
              gradientStart: lightenColor(mainColor, 30),
              gradientEnd: shadeColor(mainColor, -30)
            },
            dataByDate: {}
          };
        }

        userSeries[userId].dataByDate[dayEntry.date] = {
          userId: Number(userId),
          date: dayEntry.date,
          label: dateLabel,
          value: Number(computedValue.toFixed(2)),
          color: userSeries[userId].meta.color,
          gradientStart: userSeries[userId].meta.gradientStart,
          gradientEnd: userSeries[userId].meta.gradientEnd
        };

        if (computedValue > globalMaxValue) {
          globalMaxValue = computedValue;
        }
      });

      const aggregateValue = metric === 'orders'
        ? (dailyMinutes > 0 ? Number((dailyOrders / dailyMinutes).toFixed(2)) : 0)
        : (dailyMinutes > 0 ? Number((dailyItems / dailyMinutes).toFixed(2)) : 0);

      const aggregateGradient = {
        color: ALL_PACKERS_COLOR,
        gradientStart: lightenColor(ALL_PACKERS_COLOR, 15),
        gradientEnd: shadeColor(ALL_PACKERS_COLOR, -15)
      };

      aggregateSeries.push({
        userId: 'all',
        date: dayEntry.date,
        label: dateLabel,
        value: aggregateValue,
        ...aggregateGradient
      });

      if (aggregateValue > globalMaxValue) {
        globalMaxValue = aggregateValue;
      }
    });

    const seriesByPacker = {
      all: aggregateSeries
    };
    const packers = [];

    Object.entries(userSeries).forEach(([userId, details]) => {
      const {meta, dataByDate} = details;
      const normalizedSeries = orderedDates.map(({date, label}) => {
        if (dataByDate[date]) {
          return dataByDate[date];
        }

        return {
          userId: Number(userId),
          date,
          label,
          value: 0,
          color: meta.color,
          gradientStart: meta.gradientStart,
          gradientEnd: meta.gradientEnd
        };
      });

      seriesByPacker[userId] = normalizedSeries;
      packers.push({
        id: String(userId),
        label: meta.label,
        color: meta.color
      });
    });

    const normalizedMax = Math.max(globalMaxValue, 1);

    return {
      seriesByPacker,
      packers,
      maxValue: Math.ceil(normalizedMax * 1.1),
      success: true
    };
  } catch (error) {
    console.error('Error processing items per minute statistics:', error);
    return {
      seriesByPacker: {all: []},
      packers: [],
      maxValue: 0,
      success: false
    };
  }
};

export const truncateLabel = label => {
  if (!label) return '';

  const maxLength = 10;

  if (label.length > maxLength) {
    return `${label.substring(0, maxLength)}...`;
  }
  return label;
};

export const calculateGridLines = (maxValue, chartWidth) => {
  const segments = 6;
  const lines = [];
  const roundedMaxValue = Math.ceil(maxValue);
  
  for (let i = 0; i <= segments; i++) {
    lines.push({
      position: (i / segments) * chartWidth,
      value: (i / segments) * roundedMaxValue
    });
  }

  return lines;
};

export const getValueTextPosition = (barWidth, itemValue) => {
  return {
    x: barWidth / 2,
    anchor: 'middle',
    fill: 'white',
    display: true
  };
};

export const calculateOptimalDisplayTime = (chartData, chartType, maxBarsVisible, barWidthWithSpacing = 100, scrollSpeed = 35) => {
  let baseTime = 8;
  
  if (!chartData || Object.keys(chartData).length === 0) {
    return baseTime;
  }

  let result = baseTime;
  let dataCount = 0;

  switch (chartType) {
    case '15min': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      break;
    }
    
    case 'daily': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      break;
    }
    
    case 'horizontal': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      scrollSpeed = 15;
      break;
    }
    
    case 'todayVsYesterday': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      baseTime = dataCount > 6 ? 11 : 8;
      break;
    }
    
    case 'weekly': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      break;
    }

    case 'itemsPerMinute': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      break;
    }

    case 'itemsPerHour': {
      dataCount = Array.isArray(chartData) ? chartData.length : 0;
      break;
    }
  }

  if (dataCount > maxBarsVisible) {
    const additionalBars = dataCount - maxBarsVisible;
    const timePerBar = barWidthWithSpacing / scrollSpeed;
    result = baseTime + (additionalBars * timePerBar) - 5;
    result = Math.ceil(result);
  }

  return result;
};

export const processItemsPerHourStatistics = (response, metric = 'items') => {
  if (!response?.data?.statistics) {
    return {
      seriesByPacker: {all: []},
      packers: [],
      maxValue: 0,
      success: false
    };
  }

  try {
    const {statistics: statisticsList = [], user_details: userDetails = []} =
      response.data;

    if (!statisticsList.length) {
      return {
        seriesByPacker: {all: []},
        packers: [],
        maxValue: 0,
        success: true
      };
    }

    const userSeries = {};
    const aggregateSeries = [];
    let globalMaxValue = 0;
    const hourMap = {};

    statisticsList.forEach(userStat => {
      const userId = parseInt(userStat.user_id, 10);
      const userName = userStat.user_name;
      const userColor = userStat?.color;

      if (!userSeries[userId]) {
        const mainColor = userColor;
        userSeries[userId] = {
          meta: {
            label: userName,
            color: mainColor,
            gradientStart: lightenColor(mainColor, 30),
            gradientEnd: shadeColor(mainColor, -30)
          },
          dataByHour: {}
        };
      }

      const hourlyBreakdown = userStat.hourly_breakdown || [];
      hourlyBreakdown.forEach(hourEntry => {
        const hour = hourEntry.hour || '';
        let value = 0;

        if (metric === 'orders') {
          const orderData = hourEntry.data || [];
          const uniqueOrders = new Set();
          orderData.forEach(order => {
            if (order.order_increment_id && order.status === 'scanned') {
              uniqueOrders.add(order.order_increment_id);
            }
          });
          value = uniqueOrders.size;
        } else if (metric === 'items') {
          value = hourEntry.scanned_item_count || hourEntry.scan_order_item_count || hourEntry.items_scanned || 0;
        } else {
          value = hourEntry.items_scanned || 0;
        }

        if (!hourMap[hour]) {
          hourMap[hour] = true;
        }

        userSeries[userId].dataByHour[hour] = {
          userId: userId,
          hour: hour,
          label: formatHourTo12Hour(hour),
          value: value,
          color: userSeries[userId].meta.color,
          gradientStart: userSeries[userId].meta.gradientStart,
          gradientEnd: userSeries[userId].meta.gradientEnd
        };

        if (value > globalMaxValue) {
          globalMaxValue = value;
        }
      });
    });

    const sortedHours = Object.keys(hourMap).sort();

    sortedHours.forEach(hour => {
      const stacksForHour = [];
      let aggregateValue = 0;
      let totalItems = 0;
      const allUniqueOrders = new Set();

      statisticsList.forEach(userStat => {
        const userId = parseInt(userStat.user_id, 10);
        const hourlyBreakdown = userStat.hourly_breakdown || [];
        const hourEntry = hourlyBreakdown.find(h => h.hour === hour);
        if (hourEntry) {
          let userValue = 0;
          if (metric === 'orders') {
            const orderData = hourEntry.data || [];
            const uniqueOrders = new Set();
            orderData.forEach(order => {
              if (order.order_increment_id && order.status === 'scanned') {
                uniqueOrders.add(order.order_increment_id);
                allUniqueOrders.add(order.order_increment_id);
              }
            });
            userValue = uniqueOrders.size;
          } else if (metric === 'items') {
            userValue = hourEntry.scanned_item_count || hourEntry.scan_order_item_count || hourEntry.items_scanned || 0;
            totalItems += userValue;
          } else {
            userValue = hourEntry.items_scanned || 0;
            totalItems += userValue;
          }

          if (userValue > 0) {
            const userInfo = userSeries[userId];
            if (userInfo) {
              stacksForHour.push({
                userId: userId,
                value: userValue,
                name: userInfo.meta.label,
                color: userInfo.meta.color,
                gradientStart: userInfo.meta.gradientStart,
                gradientEnd: userInfo.meta.gradientEnd
              });
            }
          }
        }
      });
      stacksForHour.sort((a, b) => a.value - b.value);
      aggregateValue = metric === 'orders' ? allUniqueOrders.size : totalItems;
      aggregateSeries.push({
        userId: 'all',
        hour: hour,
        label: formatHourTo12Hour(hour),
        value: aggregateValue,
        stacks: stacksForHour,
        total: aggregateValue,
        isStacked: true
      });
      if (aggregateValue > globalMaxValue) {
        globalMaxValue = aggregateValue;
      }
    });

    const seriesByPacker = {
      all: aggregateSeries
    };
    const packers = [];

    Object.entries(userSeries).forEach(([userId, details]) => {
      const {meta, dataByHour} = details;
      const normalizedSeries = sortedHours.map(hour => {
        if (dataByHour[hour]) {
          return dataByHour[hour];
        }

        return {
          userId: Number(userId),
          hour,
          label: formatHourTo12Hour(hour),
          value: 0,
          color: meta.color,
          gradientStart: meta.gradientStart,
          gradientEnd: meta.gradientEnd
        };
      });

      seriesByPacker[userId] = normalizedSeries;
      packers.push({
        id: String(userId),
        label: meta.label,
        color: meta.color
      });
    });

    const normalizedMax = Math.max(globalMaxValue, 1);

    return {
      seriesByPacker,
      packers,
      maxValue: Math.ceil(normalizedMax * 1.1),
      success: true
    };
  } catch (error) {
    console.error('Error processing items per hour statistics:', error);
    return {
      seriesByPacker: {all: []},
      packers: [],
      maxValue: 0,
      success: false
    };
  }
};

export const getScrollDuration = (distance, speed) => {
  return (distance / speed) * 1000;
};

export const stopAnimation = (animationRef) => {
  if (animationRef.current) {
    animationRef.current.stop();
  }
};

export const createScrollAnimation = ({
  scrollAnimatedValue,
  animationRef,
  toValue,
  duration,
  easing,
  autoScrollEnabled,
  pauseAtEnds,
  onComplete,
  delay = 0
}) => {
  stopAnimation(animationRef);

  const startAnimation = () => {
    animationRef.current = Animated.timing(scrollAnimatedValue, {
      toValue: toValue,
      duration: duration,
      easing: easing,
      useNativeDriver: true,
    });

    animationRef.current.start(({ finished }) => {
      if (finished && autoScrollEnabled.current) {
        setTimeout(() => {
          if (autoScrollEnabled.current && onComplete) {
            onComplete();
          }
        }, pauseAtEnds);
      }
    });
  };

  if (delay > 0) {
    setTimeout(startAnimation, delay);
  } else {
    startAnimation();
  }
};
